/**
 * Domain types shared by the app and the sync engine. They mirror the
 * Supabase tables in supabase/migrations (company = one ISP, the security
 * boundary; terminals are data inside a company).
 */

export type UUID = string;
export type ISODate = string; // YYYY-MM-DD
export type ISOTimestamp = string; // RFC3339

/**
 * owner: pays, transfers ownership · admin: settings, recipients, exports,
 * deletes · manager: day-to-day manager tools · driver: own requests & forms ·
 * mechanic: only work orders flagged for mechanics (+ trucks, parts, mileage);
 * one mechanic login can serve several companies.
 */
export type Role = 'owner' | 'admin' | 'manager' | 'driver' | 'mechanic';
export const MANAGER_ROLES: readonly Role[] = ['owner', 'admin', 'manager'];
export const ADMIN_ROLES: readonly Role[] = ['owner', 'admin'];
export const isManagerRole = (r: Role | null | undefined): boolean => !!r && MANAGER_ROLES.includes(r);
export const isAdminRole = (r: Role | null | undefined): boolean => !!r && ADMIN_ROLES.includes(r);

/** Columns every synced, company-scoped table shares (server: add_standard_columns). */
export interface BaseRow {
  id: UUID;
  company_id: UUID;
  created_by: UUID | null;
  created_at: ISOTimestamp;
  updated_at: ISOTimestamp;
  deleted_at: ISOTimestamp | null;
}

// ─── Companies & people ─────────────────────────────────────────────────────
export type SubscriptionStatus = 'trialing' | 'active' | 'grace' | 'lapsed' | 'cancelled' | 'comped';

export interface Company {
  id: UUID;
  name: string;
  slug: string;
  timezone: string;
  fedex_csa_name: string;
  logo_path: string | null;
  brand_color: string | null;
  plan: string;
  subscription_status: SubscriptionStatus;
  trial_ends_at: ISOTimestamp | null;
  features: Record<string, boolean>;
  lineup_public_token: string;
}

export interface Membership {
  id: UUID;
  company_id: UUID;
  user_id: UUID;
  role: Role;
  status: 'active' | 'terminated';
  phone: string;
  hired_on: ISODate | null;
  terminated_on: ISODate | null;
}

export interface Person {
  user_id: UUID;
  role: Role;
  status: 'active' | 'terminated';
  display_name: string;
  email: string;
  phone: string;
  hired_on: ISODate | null;
  terminated_on: ISODate | null;
  /** Set for a mechanic shop's tech (0042): the shop adds and removes them, not the company. */
  shop_name?: string | null;
}

export interface Profile {
  user_id: UUID;
  display_name: string;
}

/** Free-form company settings (company_settings key/value); typed keys are added as features land. */
export interface CompanySettings {
  [key: string]: unknown;
}

// ─── Synced tables used offline (see supabase/migrations/0009_sync.sql) ─────
export interface TerminalRow extends BaseRow { name: string; code: string; address: string; timezone: string | null; active: boolean }
/** Which terminals a person works at (a filter, not a permission). */
export interface MemberTerminalRow extends BaseRow { user_id: UUID; terminal_id: UUID; is_home: boolean }
export interface TruckRow extends BaseRow {
  unit_number: string; fedex_vehicle_id: string; vin: string; plate: string; make: string; model: string;
  year: number | null; terminal_id: UUID | null; active: boolean; notes: string;
}
export type MaintenanceStatus = 'submitted' | 'cancelled' | 'acknowledged' | 'in_progress' | 'done' | 'declined';
export interface MaintenanceRequestRow extends BaseRow {
  user_id: UUID | null; truck_id: UUID | null; category: string; description: string; photos: string[];
  status: MaintenanceStatus; work_order_id: UUID | null; manager_note: string | null; status_by: UUID | null; status_at: ISOTimestamp | null;
}
export type TimeoffStatus = 'pending' | 'approved' | 'denied' | 'cancelled';
/** Storage path for a new file: `{company}/{user}/{kind}/{id}.{ext}` (kind e.g. maintenance, accident). */
export function storagePath(companyId: UUID, userId: UUID, kind: string, id: string, ext = 'jpg'): string {
  return `${companyId}/${userId}/${kind}/${id}.${ext}`;
}

export type TimeoffKind = 'vacation' | 'personal' | 'sick' | 'unpaid' | 'other';
/** week = a work week · days = single day(s) · open = no dates yet ("any week in August"). */
export type TimeoffSpan = 'week' | 'days' | 'open';
export interface TimeoffRequestRow extends BaseRow {
  user_id: UUID | null; span: TimeoffSpan; start_date: ISODate | null; end_date: ISODate | null; open_request: string;
  backup_start: ISODate | null; kind: TimeoffKind;
  note: string; status: TimeoffStatus; decided_by: UUID | null; decided_at: ISOTimestamp | null; decision_note: string | null;
}
/** Company calendar: holidays, events and blackout days (managers write, everyone reads). */
export interface CalendarEventRow extends BaseRow {
  title: string; start_date: ISODate; end_date: ISODate; kind: 'event' | 'blackout' | 'holiday'; notes: string;
}
/** A FedEx route the company runs (optionally tied to a terminal). */
export interface RouteRow extends BaseRow { terminal_id: UUID | null; code: string; name: string; active: boolean; sort_order: number }
/** Who drives which route on a day (truck optional). */
export interface ScheduleAssignmentRow extends BaseRow {
  work_date: ISODate; route_id: UUID | null; user_id: UUID | null; truck_id: UUID | null; terminal_id: UUID | null; note: string;
}
/** A driver can't (or can) work a specific day or a standing weekday (0 = Sunday). */
export interface ScheduleAvailabilityRow extends BaseRow { user_id: UUID | null; work_date: ISODate | null; weekday: number | null; available: boolean; note: string }
/** Someone left off the schedule builder (office staff, long leave). */
export interface ScheduleExcludedRow extends BaseRow { user_id: UUID }
/** A day switched on/off (and its note) — for one route, or the whole company when route_id is null. */
export interface ScheduleDayRow extends BaseRow { work_date: ISODate; route_id: UUID | null; open: boolean; note: string }
/** row = spots side by side (e.g. bulk spots) · belt = two columns either side of the conveyor belt. */
export type LineupLayout = 'row' | 'belt';
/** A van line at a terminal (a terminal has many; a company may use several). */
export interface LineupLineRow extends BaseRow { terminal_id: UUID | null; name: string; sort_order: number }
export interface LineupSectionRow extends BaseRow {
  terminal_id: UUID | null; name: string; sort_order: number; layout: LineupLayout; spot_prefix: string;
  /** The van line this part of the board is on (null = shown with every line). */
  line_id: UUID | null;
}
export interface LineupSpotRow extends BaseRow {
  section_id: UUID; label: string; sort_order: number; allowed_truck_ids: UUID[] | null;
  /** Which side of the belt (belt sections only). */
  side: 'left' | 'right' | null;
}
export interface LineupAssignmentRow extends BaseRow { spot_id: UUID; truck_id: UUID }
/** A uniform item the company hands out (empty `sizes` = free-text size, e.g. pants "32×30"). */
export interface UniformItemRow extends BaseRow { name: string; sizes: string[]; active: boolean; sort_order: number; max_qty: number; size_hint: string }
export type UniformStatus = 'requested' | 'cancelled' | 'ordered' | 'ready' | 'delivered' | 'declined';
/** One item of a uniform request; items sent together share `batch_id`. */
export interface UniformRequestRow extends BaseRow {
  user_id: UUID | null; item_id: UUID | null; size: string; qty: number; note: string; status: UniformStatus;
  manager_note: string | null; status_by: UUID | null; status_at: ISOTimestamp | null; batch_id: UUID | null;
}
/** A driver's own profile in a company: contact, emergency contact, uniform sizes keyed by item id. */
export interface DriverProfileRow extends BaseRow {
  user_id: UUID | null; phone: string; emergency_name: string; emergency_phone: string; sizes: Record<string, string>;
}
export type VehicleDocKind = 'barcode' | 'insurance' | 'registration' | 'dot_inspection' | 'lease' | 'other' | 'fleet_insurance';
/** A document for one truck (managers upload; everyone can open). */
/** A truck's document — or, with kind 'fleet_insurance', the company's fleet policy for one state (no truck). */
export interface VehicleDocumentRow extends BaseRow { truck_id: UUID | null; title: string; file_path: string; expires_on: ISODate | null; kind: VehicleDocKind; state: string }
/** A truck a driver keeps at the top of Vehicle documents (two at most). */
export interface TruckFavoriteRow extends BaseRow { user_id: UUID | null; truck_id: UUID }
export type AccidentStatus = 'draft' | 'submitted' | 'reviewed';
/** The driver's accident report; the form (the Driver Portal's 9 steps) lives in `data`. */
export interface AccidentReportRow extends BaseRow {
  user_id: UUID | null; truck_id: UUID | null; status: AccidentStatus; occurred_at: ISOTimestamp | null; location: string;
  data: Record<string, unknown>; submitted_at: ISOTimestamp | null; reviewed_by: UUID | null; reviewed_at: ISOTimestamp | null; pdf_path: string | null;
}
export type AccidentPhotoCategory = 'my_vehicle' | 'other_vehicle' | 'scene' | 'other';
export interface AccidentPhotoRow extends BaseRow { user_id: UUID | null; report_id: UUID; category: AccidentPhotoCategory; file_path: string; caption: string }
export type Op201Status = 'submitted' | 'cancelled' | 'sent' | 'failed' | 'superseded';
/** A FedEx OP-201 signature release; the form lives in `data`. */
export interface Op201FormRow extends BaseRow {
  user_id: UUID | null; form_version: string; data: Record<string, unknown>; signature_path: string | null; status: Op201Status;
  pdf_path: string | null; sent_to: string[] | null; sent_at: ISOTimestamp | null; expires_on: ISODate | null; renewed_from: UUID | null;
}
export interface ShipperRow extends BaseRow { name: string; address: string; account: string; active: boolean; route_id: UUID | null }
export interface ShipperContactRow extends BaseRow { shipper_id: UUID; name: string; phone: string; email: string; signature_path: string | null }
export interface BulkPickupRow extends BaseRow {
  user_id: UUID | null; shipper_id: UUID | null; contact_id: UUID | null; pickup_date: ISODate; packages: number | null;
  data: Record<string, unknown>; status: 'submitted' | 'cancelled' | 'sent' | 'failed'; sent_to: string[] | null; sent_at: ISOTimestamp | null; route_id: UUID | null;
}
/** A company's handbook; one is active at a time. */
export interface HandbookVersionRow extends BaseRow { version: string; title: string; file_path: string; published_at: ISOTimestamp | null; active: boolean; ack_text: string; link_url: string }
/** Who a manager's message is for: everyone, picked people, or everyone at some terminals. */
export interface BroadcastAudience { all?: boolean; user_ids?: UUID[]; terminal_ids?: UUID[] }
/** A message from a manager (sent the moment it's saved — the server stamps sent_at and pushes it). */
export interface BroadcastRow extends BaseRow { title: string; body: string; audience: BroadcastAudience; sent_at: string | null }
/** "I've opened this message." One per person per message. */
export interface BroadcastReadRow extends BaseRow { user_id: UUID; broadcast_id: UUID; read_at: string }
export interface HandbookAckRow extends BaseRow {
  user_id: UUID | null; handbook_version_id: UUID; typed_name: string; signature_path: string | null; ip: string; user_agent: string; signed_at: ISOTimestamp;
}
/** A consent document (e.g. camera / biometric); one active per kind. */
export interface ConsentVersionRow extends BaseRow { kind: string; version: string; title: string; body_text: string; published_at: ISOTimestamp | null; active: boolean; policy_url: string }
export interface ConsentSignatureRow extends BaseRow {
  user_id: UUID | null; consent_version_id: UUID; typed_name: string; signature_path: string | null; ip: string; user_agent: string; signed_at: ISOTimestamp;
}
// ─── Maintenance (managers + mechanics) ─────────────────────────────────────
export type WorkOrderStatus = 'open' | 'in_progress' | 'waiting_parts' | 'done' | 'cancelled';
export type WorkOrderPriority = 'low' | 'normal' | 'high' | 'urgent';
/** A card note ("Parts ordered, ETA Thursday"), newest last. */
export interface WorkNote { at: ISOTimestamp; by: UUID | null; name: string; text: string }
/** A shop job. `number` (WO #12) is set by the server; null until the job has synced. */
export interface WorkOrderRow extends BaseRow {
  number: number | null; truck_id: UUID | null; title: string; description: string; status: WorkOrderStatus; priority: WorkOrderPriority;
  vendor: string; cost_cents: number | null; odometer: number | null; opened_on: ISODate; completed_on: ISODate | null; completed_by: UUID | null;
  work_done: string; notes: WorkNote[]; for_mechanic: boolean; mechanic_id: UUID | null; include_mmr: boolean;
  reported_by: UUID | null; recurring_task_id: UUID | null; due_on: ISODate | null;
  /** Mileage rules (0045): the odometer it's due at, and the date limit from the rule's days. due_on = the earlier of the two (estimated). */
  due_miles?: number | null; due_calendar?: ISODate | null;
  /** Which of the shop's techs is on it — only the shop owner sets it (0042). */
  shop_tech_id?: UUID | null;
}
export interface PartRow extends BaseRow { name: string; part_number: string; vendor: string; qty_on_hand: number; cost_cents: number | null; truck_id: UUID | null; notes: string; brand: string; last_ordered: ISODate | null }
export interface OdometerReadingRow extends BaseRow { truck_id: UUID; reading: number; read_on: ISODate; month: ISODate; source: string }
/** A recurring service rule; `applies_to_all` = every active truck, else the trucks in recurring_task_trucks. */
export interface RecurringTaskRow extends BaseRow { name: string; description: string; interval_days: number | null; interval_miles: number | null; active: boolean; applies_to_all: boolean }
export interface RecurringTaskTruckRow extends BaseRow {
  task_id: UUID; truck_id: UUID; last_done_on: ISODate | null; last_done_miles: number | null; next_due_on: ISODate | null; next_due_miles: number | null;
}

/** One card on a resource page: a web/email/phone link or a file in company-assets (`{company}/{user}/resources/…`). */
export interface ResourceLink { id: string; label: string; desc: string; url?: string; file_path?: string }
/** A company's Benefits / Driver resources page (managers edit; everyone reads). */
export interface ResourcePageRow extends BaseRow { slug: string; title: string; body_md: string; links: ResourceLink[]; sort_order: number }
export interface DocumentTypeRow extends BaseRow {
  code: string; label: string; requires_expiry: boolean; alert_days: number; active: boolean; sort_order: number;
  /** false = the manager tracks only the date (e.g. safety training); drivers can't add or change it. */
  driver_uploads: boolean;
}
export interface DriverDocumentRow extends BaseRow {
  user_id: UUID | null; document_type_id: UUID | null; file_path: string; expires_on: ISODate | null; number: string;
  verified_by: UUID | null; verified_at: ISOTimestamp | null; manager_note: string | null;
}

// ─── Sync ───────────────────────────────────────────────────────────────────
/**
 * Name of a synced, company-scoped table. The list the app syncs is
 * `SYNC_TABLES` in local/db.ts; the server's whitelist is private.sync_tables.
 */
export type SyncTable = string;

export type OutboxOp = 'upsert' | 'delete' | 'restore';

export interface OutboxEntry {
  id: UUID;
  table: SyncTable;
  op: OutboxOp;
  row_id: UUID;
  row: Record<string, unknown>;
  queued_at: number; // epoch ms
  attempts: number;
  last_error: string | null;
  /** Server rejected this permanently (RLS/409). Surfaced, never retried. 0/1 because IndexedDB cannot index booleans. */
  rejected: 0 | 1;
}

export interface SyncStateRow {
  table: SyncTable;
  last_pulled_at: ISOTimestamp | null;
}

export type SyncStatusState = 'synced' | 'pending' | 'offline' | 'error' | 'paused';

export interface SyncStatus {
  state: SyncStatusState;
  pending: number;
  rejected: number;
  lastError: string | null;
  lastSyncAt: number | null;
}
